import { QueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';

import withLazyLoading from '../../hoc/withLazyLoading';
import { ACCOUNT_ROUTES } from '../../navigation/paths';
import LogbookTemplates from '../../pages/logBooks/components/LogbookTemplates';
import { logbookTemplateLoader } from '../../pages/logBooks/hooks/useLogbookTemplateTable';
import { logbookConfigurationLoader, LogbookConfigurationPage } from '../../pages/logbookConfiguration';
import LogbookList from '../../pages/logbookConfiguration/components/LogbookList';
import { logbookListLoader } from '../../pages/logbookConfiguration/hooks/useLogbookListTable';
import { OcrDataInputPage } from '../../pages/ocrDataInput';
import { createCrumbHandle, withCrumbParents } from '../../utilities/auth';
import withPostHogTracker from '../../composeRouter';

const Profile = withLazyLoading(() => import('../../pages/Profile'));
const VideoTutorial = withLazyLoading(() => import('../../pages/VideoTutorial'));
const AboutPage = withLazyLoading(() => import('../../pages/AboutPage'));
const DataCorrection = withLazyLoading(
    () => import('../../components/DataCorrection/DataCorrection')
);
const Triggers = withLazyLoading(
    () => import('../../components/Triggers/TriggerListPage/TriggerListpage')
);
const CreateEditTrigger = withLazyLoading(
    () => import('../../components/Triggers/CreateEditTrigger/CreateEditTrigger')
);
const SystemPerformance = withLazyLoading(
    () => import("../../components/SystemPerformance/components/SystemPerformanceView"),
)

const miscRoutes = (queryClient: QueryClient) => [
    {
        path: ACCOUNT_ROUTES.root,
        element: <Navigate to={ACCOUNT_ROUTES.contact} replace />,
    },
    {
        path: ACCOUNT_ROUTES.pictureAndDashboard,
        element: <Profile />,
        handle: withCrumbParents(createCrumbHandle('Profile picture and dashboard', 'breadcrumbs.pictureAndDashboard'), [
            { label: 'Profile', pathname: ACCOUNT_ROUTES.contact },
        ]),
    },
    {
        path: '/video-tutorial',
        element: <VideoTutorial />,
        // authz-v2 TODO: VideoTutorials_View_R — gate server-side / useCan at component level
        handle: createCrumbHandle('Video Tutorials'),
    },
    {
        path: 'aboutPage',
        element: <AboutPage />,
        handle: createCrumbHandle('About'),
    },
    {
        path: '/Data-Correction',
        element: <DataCorrection />,
        // authz-v2 TODO: DataCorrection_Manage_WE — gate server-side / useCan at component level
        handle: createCrumbHandle('Data Correction'),
    },
    {
        path: '/Triggers',
        handle: createCrumbHandle('Triggers'),
        children: [
            {
                index: true,
                element: withPostHogTracker(<Triggers />),
                // authz-v2 TODO: Trigger_View_R — gate server-side / useCan at component level
            },
            {
                path: 'CreateEditTrigger/:id?',
                element: withPostHogTracker(<CreateEditTrigger />),
                // authz-v2 TODO: Trigger_Manage_A — gate server-side / useCan at component level
                handle: createCrumbHandle('Create/Edit Trigger'),
            },
        ]
    },
    {
        path: "/systemPerformance",
        element: <SystemPerformance />,
        handle: createCrumbHandle('System Performance')
    },
    {
        path: "/logbookTemplates",
        element: <LogbookTemplates />,
        handle: createCrumbHandle('Logbook Templates'),
        loader: logbookTemplateLoader(queryClient)
    },
    {
        path: "/logbooks/:plantId",
        handle: createCrumbHandle('Log Books'),
        children: [
            {
                index: true,
                element: withPostHogTracker(<LogbookList />),
                loader: logbookListLoader(queryClient),
            },
            {
                path: 'logbook-configuration/add',
                element: <LogbookConfigurationPage />,
                handle: createCrumbHandle('Configure Logbook'),
            },
            {
                path: 'logbook-configuration/:logbookAssetConfigurationId',
                element: <LogbookConfigurationPage />,
                handle: createCrumbHandle('Configure Logbook'),
                loader: logbookConfigurationLoader,
            },
        ]
    },
    {
        path: 'logbook-data-input',
        element: withPostHogTracker(<OcrDataInputPage />),
        handle: createCrumbHandle('Input Logbook Data'),
    },
];

export default miscRoutes;
